<template>
    <div class="global-map">
        <div ref="map" class="global-map__map">
            <div class="embedguard" v-if="showMapGuard">
                <div class="embedguard__content">
                    <h3 v-html="guardHeadline" />
                    <p class="ignore-dependencies" v-html="guardIntro" />
                    <div class="form-group">
                        <div class="input checkbox checkbox--switch">
                            <div class="form-check">
                                <label class="add-on form-check-label">
                                    <input ref="checkbox" type="checkbox" @change="acceptConsent">
                                    <span v-html="guardLabel" />
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <global-info-dealer class="global-info--map-reference" ref="infoWindowDealer" @choose="dealerChosen($event)"/>
    </div>
</template>

<script>
import gmaps from "@global/utilities/global-gmaps";
import GeoService from "@global/utilities/global-geo-service";
import GlobalInfoDealer from "@global/global-elements/global-info-item/global-info-dealer";
import {acceptConsent, consentPromise, hasConsent, UserCentricIds} from '@utilities/sal-usercentrics/sal-usercentrics';
import markerIcon from './marker-origin.png';

export default {
    name: "global-map",
    components: {GlobalInfoDealer},
    props: {
        origin: {
            type: Object,
            validator: (value) => {
                const keys = Object.keys(value);
                return keys.indexOf('lat') !== -1 && keys.indexOf('lng') !== -1
            },
            required: true
        },
        zoom: {
            type: Number,
            validator: (value) => {
                return value <= 16 && value >= 1;
            }
        },
        marker: null,
        markerType: {
            type: String,
            default: 'dealer',
            validator(value) {
                return ['dealer', 'html'].indexOf(value) !== -1
            },
        },
        userLocation: null
    },
    data() {
        return {
            showMapGuard: false,
            googleMaps: null,
            map: null,
            mapOptions: {
                center: {lat: this.origin.latitude, lng: this.origin.longitude},
                zoom: this.zoom,
                maxZoom: 16,
                disableDefaultUI: true,
                fullscreenControl: true,
                zoomControl: true
            },
            markerReferences: [],
            guardHeadline: window.jsI18N && window.jsI18N.mapguard && window.jsI18N.mapguard.headline ? window.jsI18N.mapguard.headline : '',
            guardIntro: window.jsI18N && window.jsI18N.mapguard && window.jsI18N.mapguard.intro ? window.jsI18N.mapguard.intro : '',
            guardLabel: window.jsI18N && window.jsI18N.mapguard && window.jsI18N.mapguard.label ? window.jsI18N.mapguard.label.replace(/LINK\[(.*)]/i, '<a href="#" onClick="showConsentDetails(\''+UserCentricIds.GOOGLE_MAPS+'\')">$1</a>') : ''
        }
    },
    computed: {},
    watch: {
        origin(newValue) {
            if (this.map) {
                this.map.panTo(newValue);
            }
        },
        marker(newValue) {
            this.setMarkers(newValue)
            if (newValue === null) {
                this.map.panTo(this.origin);
            }
        },
        userLocation(newValue) {
            this.setMarkers(this.marker)

            if (newValue === null) {
                this.map.panTo(this.origin);
            }
        }
    },
    methods: {
        acceptConsent() {
            acceptConsent(UserCentricIds.GOOGLE_MAPS);
        },
        setMarkers(marker) {
            if (!this.googleMaps) {
                return;
            }

            this.clearMarkers();
            const infoWindow = new this.googleMaps.maps.InfoWindow();
            const bounds = new this.googleMaps.maps.LatLngBounds();
            const map = this.map;

            if (marker) {
                marker.forEach((marker, index) => {
                    const markerObject = new this.googleMaps.maps.Marker({
                        position: GeoService.normalizeLatLng(marker.geo),
                        icon: `/_marker/${index + 1}`,
                    });

                    markerObject.setMap(map)

                    bounds.extend(markerObject.getPosition());
                    this.markerReferences.push(markerObject);

                    markerObject.addListener('click', () => {
                        if (this.markerType === 'dealer') {
                            infoWindow.setContent(this.generateDealerInfoWindow(marker));
                        }
                        if (this.markerType === 'html') {
                            marker.html ? infoWindow.setContent(marker.html) : new Error("marker.html is undefinded. Choose another marker type or add .html to the marker object");
                        }
                        infoWindow.open(map, markerObject)
                    });
                })
            }

            if (this.userLocation) {
                const originMarker = new this.googleMaps.maps.Marker({
                    position: GeoService.normalizeLatLng(this.userLocation),
                    icon: markerIcon,
                });

                originMarker.setMap(map)

                this.markerReferences.push(originMarker);

                bounds.extend(originMarker.getPosition());
            }
            infoWindow.addListener('closeclick', () => {
                map.fitBounds(bounds);
            });

            if (marker === null && this.userLocation === null) {
                map.panTo(this.origin)
                map.zoom = this.zoom
            } else {
                map.fitBounds(bounds);
            }

        },
        dealerChosen(dealer) {
            this.$store.dispatch('selectDealer', dealer);
        },
        generateDealerInfoWindow(dealer) {
            this.$refs.infoWindowDealer.$data.dealer = dealer;
            return this.$refs.infoWindowDealer.$el;
        },
        clearMarkers() {
            this.markerReferences.forEach(markerObject => {
                markerObject.setMap(null);
            });

            this.markers = [];
        },
        async loadGoogleMaps() {
            Object.assign(this.mapOptions, {center: this.origin, zoom: this.zoom})
            try {
                this.googleMaps = await gmaps();
            } catch (e) {
                console.error("googleMaps init failed", e)
            }

            try {
                this.map = new this.googleMaps.maps.Map(this.$refs.map, this.mapOptions)
            } catch (e) {
                console.error("Map could not be created", e)
            }

            if (this.marker) {
                this.setMarkers(this.marker)
            }
        }
    },
    mounted() {
        if (hasConsent(UserCentricIds.GOOGLE_MAPS)) {
            this.loadGoogleMaps();
        }
        else {
            consentPromise(UserCentricIds.GOOGLE_MAPS).then(() => {
                this.loadGoogleMaps();
            });
            this.showMapGuard = true;
        }
    }
}
</script>
